import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import useCallPermission from '../hooks/useCallPermission';
import callService from '../services/CallService';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

function waitForIceGathering(pc, timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timeout = setTimeout(resolve, timeoutMs);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timeout);
        resolve();
      }
    });
  });
}

function CallButton({ serviceId, contactId, phoneNumber, contactName, variant = 'icon', onCallEnded }) {
  const {
    loading,
    showCallButton,
    showRequestButton,
    permissionStatus,
    expiresAt,
    unavailableReason,
    sendRequest,
    sendingRequest,
    refreshPermission,
    requestRemaining24h,
    requestRemaining7d
  } = useCallPermission(contactId, phoneNumber);

  const unavailableTooltip = unavailableReason
    || (permissionStatus === 'pending'
      ? 'Waiting for customer to accept call permission'
      : 'Call permission not available');

  const [callState, setCallState] = useState('idle');
  const [currentCallId, setCurrentCallId] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const statusPollIntervalRef = useRef(null);
  const sdpPollIntervalRef = useRef(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatExpiration = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString();
  };

  const stopStatusPolling = () => {
    if (statusPollIntervalRef.current) {
      clearInterval(statusPollIntervalRef.current);
      statusPollIntervalRef.current = null;
    }
  };

  const cleanupWebRTC = () => {
    stopStatusPolling();
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    if (sdpPollIntervalRef.current) clearInterval(sdpPollIntervalRef.current);
  };

  const startTimer = () => {
    let seconds = 0;
    setCallDuration(0);
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    durationIntervalRef.current = setInterval(() => {
      seconds += 1;
      setCallDuration(seconds);
    }, 1000);
  };

  const stopTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  const handleTerminalStatus = (status) => {
    stopStatusPolling();
    if (status === 'ACCEPTED') {
      setCallState('connected');
      startTimer();
      showSnackbar('Call connected!', 'success');
      return;
    }
    setCallState('idle');
    setCurrentCallId(null);
    stopTimer();
    cleanupWebRTC();
    if (onCallEnded) onCallEnded();

    if (status === 'REJECTED') {
      showSnackbar('Customer rejected the call', 'error');
    } else if (status === 'COMPLETED' || status === 'TERMINATED_BY_BUSINESS') {
      showSnackbar('Call ended', 'info');
    } else if (status === 'FAILED') {
      showSnackbar('Call failed', 'error');
    }
  };

  const startPollingCallStatus = (callId) => {
    stopStatusPolling();

    statusPollIntervalRef.current = setInterval(async () => {
      if (!callId) return;

      try {
        const statusData = await callService.getCallStatus(serviceId, callId);
        if (!statusData?.status) return;

        if (['ACCEPTED', 'REJECTED', 'COMPLETED', 'FAILED', 'TERMINATED_BY_BUSINESS'].includes(statusData.status)) {
          handleTerminalStatus(statusData.status);
        }
      } catch (error) {
        console.error('Status poll error:', error);
      }
    }, 2000);
  };

  const startPollingSdpAnswer = async (callId) => {
    let attempts = 0;
    const maxAttempts = 30;

    return new Promise((resolve, reject) => {
      if (sdpPollIntervalRef.current) clearInterval(sdpPollIntervalRef.current);

      sdpPollIntervalRef.current = setInterval(async () => {
        attempts += 1;

        try {
          const answerData = await callService.getSdpAnswer(serviceId, contactId);

          if (answerData?.sdpAnswer && peerConnectionRef.current) {
            await peerConnectionRef.current.setRemoteDescription({
              type: 'answer',
              sdp: answerData.sdpAnswer,
            });

            clearInterval(sdpPollIntervalRef.current);
            sdpPollIntervalRef.current = null;

            setCallState('ringing');
            showSnackbar('Ringing customer...', 'info');
            startPollingCallStatus(callId);
            resolve();
            return;
          }
        } catch (error) {
          console.error('SDP poll error:', error);
        }

        if (attempts >= maxAttempts) {
          clearInterval(sdpPollIntervalRef.current);
          sdpPollIntervalRef.current = null;
          setCallState('idle');
          showSnackbar('Timeout waiting for answer', 'error');
          cleanupWebRTC();
          reject(new Error('Timeout'));
        }
      }, 1000);
    });
  };

  const initiateCall = async () => {
    try {
      setCallState('initiating');
      cleanupWebRTC();

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peerConnectionRef.current = pc;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        const audio = new Audio();
        audio.srcObject = event.streams[0];
        audio.play().catch((e) => console.error('Audio error:', e));
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);

      const sdpOffer = pc.localDescription?.sdp || offer.sdp;
      const callData = await callService.initiateCall(serviceId, contactId, phoneNumber, sdpOffer);
      setCurrentCallId(callData.callId);

      await startPollingSdpAnswer(callData.callId);
    } catch (error) {
      console.error('Initiate call error:', error);
      setCallState('idle');
      const errorCode = error?.code || error.response?.data?.error?.code;
      if (errorCode === 'DUPLICATE_CALL' || error.response?.status === 409) {
        showSnackbar('User is busy or another call is in progress', 'warning');
      } else {
        showSnackbar(error?.message || error.response?.data?.error?.message || 'Failed to initiate call', 'error');
      }
      cleanupWebRTC();
    }
  };

  const endCall = async () => {
    try {
      if (currentCallId) {
        await callService.terminateCall(serviceId, currentCallId);
      }
    } catch (error) {
      console.error('Terminate call error:', error);
    } finally {
      setCallState('idle');
      setCurrentCallId(null);
      stopTimer();
      cleanupWebRTC();
      refreshPermission();
      if (onCallEnded) onCallEnded();
    }
  };

  const handleCall = () => {
    if (callState === 'connected' || callState === 'ringing') {
      endCall();
    } else {
      initiateCall();
    }
  };

  const handleSendRequest = async () => {
    const result = await sendRequest(null);

    if (result.success) {
      showSnackbar('Permission request sent successfully!', 'success');
      setTimeout(refreshPermission, 5000);
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  useEffect(() => () => {
    cleanupWebRTC();
    stopTimer();
  }, []);

  if (loading) {
    return <CircularProgress size={24} />;
  }

  if (callState === 'initiating') {
    return (
      <Button variant="contained" color="warning" disabled startIcon={<CircularProgress size={16} />}>
        Initiating...
      </Button>
    );
  }

  if (callState === 'ringing') {
    return (
      <Button
        variant="contained"
        color="warning"
        startIcon={<CallIcon />}
        endIcon={<CallEndIcon />}
        onClick={endCall}
        sx={{ backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' } }}
      >
        Ringing... (Cancel)
      </Button>
    );
  }

  if (callState === 'connected') {
    return (
      <>
        <Button
          variant="contained"
          color="success"
          startIcon={<CallIcon />}
          endIcon={<CallEndIcon />}
          onClick={endCall}
        >
          Connected {formatDuration(callDuration)}
        </Button>

        <Dialog
          open
          onClose={endCall}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              textAlign: 'center',
              p: 2,
            },
          }}
        >
          <DialogTitle>
            <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1, bgcolor: '#4caf50' }}>
              <CallIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6">Call in Progress</Typography>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body1" fontWeight="bold">
              {contactName || 'Customer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {phoneNumber}
            </Typography>

            <Typography variant="h3" sx={{ my: 2, fontFamily: 'monospace' }}>
              {formatDuration(callDuration)}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
            <Button
              variant="outlined"
              startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
              onClick={toggleMute}
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<CallEndIcon />}
              onClick={endCall}
            >
              End Call
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (showCallButton) {
    const isTemporary = permissionStatus === 'temporary';
    const expiryDate = expiresAt ? formatExpiration(expiresAt) : '';
    const isExpiringSoon = isTemporary && expiresAt
      && (new Date(expiresAt) - new Date()) < 2 * 24 * 60 * 60 * 1000;

    if (variant === 'icon') {
      return (
        <>
          <Tooltip title={isTemporary ? `Permission expires on ${expiryDate}` : `Call ${contactName || phoneNumber}`}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCall}
              sx={{
                minWidth: '36px',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                p: 0,
                '&:hover': { transform: 'scale(1.05)' },
              }}
            >
              <CallIcon fontSize="small" />
              {isExpiringSoon && (
                <AccessTimeIcon
                  sx={{ position: 'absolute', bottom: -4, right: -4, fontSize: 12, color: 'warning.main' }}
                />
              )}
            </Button>
          </Tooltip>
          <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
        </>
      );
    }

    return (
      <>
        <Button variant="contained" color="primary" startIcon={<CallIcon />} onClick={handleCall}>
          Call {contactName}
        </Button>
        <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
      </>
    );
  }

  if (showRequestButton) {
    if (variant === 'icon') {
      return (
        <>
          <Tooltip title={(
            <div>
              <div>Request permission to call</div>
              <div style={{ fontSize: '11px' }}>
                Remaining: {requestRemaining24h ?? '-'} today, {requestRemaining7d ?? '-'} this week
              </div>
            </div>
          )}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSendRequest}
              disabled={sendingRequest}
              aria-label="Request call permission"
              sx={{
                minWidth: 'auto',
                height: '36px',
                borderRadius: '18px',
                px: 1.25,
                gap: 0.5,
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {sendingRequest ? <CircularProgress size={16} /> : (
                <>
                  <HelpOutlineIcon sx={{ fontSize: 16 }} />
                  Request
                </>
              )}
            </Button>
          </Tooltip>
          <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
        </>
      );
    }

    return (
      <>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<HelpOutlineIcon />}
          onClick={handleSendRequest}
          disabled={sendingRequest}
        >
          {sendingRequest ? <CircularProgress size={20} /> : 'Request Permission'}
        </Button>
        <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
      </>
    );
  }

  if (variant === 'icon') {
    return (
      <>
        <Tooltip title={unavailableTooltip}>
          <span>
            <Button
              variant="outlined"
              color="inherit"
              disabled
              sx={{
                minWidth: '36px',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                p: 0,
              }}
            >
              <CallIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>
        <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
      </>
    );
  }

  return (
    <>
      <Tooltip title={unavailableTooltip}>
        <span>
          <Button variant="outlined" color="inherit" disabled startIcon={<CallIcon />}>
            Call Unavailable
          </Button>
        </span>
      </Tooltip>
      <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
    </>
  );
}

function SnackbarComponent({ snackbar, setSnackbar }) {
  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
    >
      <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
}

export default CallButton;
